<div class="main-content">
	<div class="container-fluid">
		<mat-card class="feature-surface">
			<mat-card-header>
				<div mat-card-avatar class="feature-surface__icon">
					<i class="material-icons" aria-hidden="true">leaderboard</i>
				</div>
				<mat-card-title>Brand Sales by Salesperson</mat-card-title>
			</mat-card-header>
			<mat-card-content>

				<!-- Reps are pinned server-side; say so rather than letting someone
				     wonder why the list is one row long. -->
				<div class="bsr-scope" *ngIf="repgated || brandgated">
					<i class="material-icons" aria-hidden="true">lock</i>
					<span>
						<ng-container *ngIf="repgated">Showing your own sales only.</ng-container>
						<ng-container *ngIf="brandgated"> Limited to the brands assigned to you.</ng-container>
					</span>
				</div>

				<!-- ── Filters ────────────────────────────────────────────────── -->
				<div class="feature-section" [formGroup]="filterForm">
					<p class="feature-section__title">Filters</p>

					<div class="bsr-filters">
						<mat-form-field appearance="outline">
							<mat-label>From</mat-label>
							<input matInput type="date" formControlName="from">
						</mat-form-field>

						<mat-form-field appearance="outline">
							<mat-label>To</mat-label>
							<input matInput type="date" formControlName="to">
						</mat-form-field>

						<mat-form-field appearance="outline">
							<mat-label>View by</mat-label>
							<mat-select formControlName="viewby" (selectionChange)="rerun()">
								<mat-option *ngFor="let d of startdimensions" [value]="d.value">{{ d.name }}</mat-option>
							</mat-select>
						</mat-form-field>

						<mat-form-field appearance="outline">
							<mat-label>Brand</mat-label>
							<mat-select formControlName="brands" multiple appSelectAll>
								<mat-option *ngFor="let b of brands" [value]="b.manufacturer_id">{{ b.name }}</mat-option>
							</mat-select>
						</mat-form-field>

						<mat-form-field appearance="outline">
							<mat-label>Product Line</mat-label>
							<mat-select formControlName="plines" multiple appSelectAll>
								<mat-option *ngFor="let p of productlines" [value]="p.value">{{ p.name }}</mat-option>
							</mat-select>
						</mat-form-field>

						<mat-form-field appearance="outline">
							<mat-label>Category</mat-label>
							<mat-select formControlName="categories" multiple appSelectAll>
								<mat-option *ngFor="let c of categories" [value]="c.value">{{ c.name }}</mat-option>
							</mat-select>
						</mat-form-field>

						<mat-form-field appearance="outline" *ngIf="!repgated">
							<mat-label>Salesperson</mat-label>
							<mat-select formControlName="salespeople" multiple appSelectAll>
								<mat-option *ngFor="let s of salespeople" [value]="s.salesmancode">{{ s.salesmanname }}</mat-option>
							</mat-select>
						</mat-form-field>
					</div>

					<div class="bsr-actions">
						<button mat-flat-button color="accent" [disabled]="loading" (click)="rerun()">
							{{ loading ? 'Running…' : 'Run report' }}
						</button>
						<button mat-stroked-button [disabled]="loading" (click)="reset()">Reset</button>
					</div>
				</div>

				<!-- ── Ranked tree ────────────────────────────────────────────── -->
				<!-- ── KPIs ───────────────────────────────────────────────────── -->
				<div class="feature-tile-row" *ngIf="!loading && !metaLoading && roots.length">
					<app-kpi-tile
						tone="accent"
						label="net sales"
						[value]="'$' + (totals.net | number:'1.2-2')"
						[sub]="datefrom + ' → ' + dateto">
					</app-kpi-tile>
					<app-kpi-tile
						label="units"
						[value]="totals.units | number:'1.0-0'"
						[sub]="totals.invoices + ' invoices'">
					</app-kpi-tile>
					<app-kpi-tile
						[label]="'top ' + grouptitle"
						[value]="topLabel"
						[sub]="totals.count + ' ranked'">
					</app-kpi-tile>
					<app-kpi-tile
						label="average"
						[value]="'$' + ((totals.net / totals.count) | number:'1.0-0')"
						[sub]="'per ' + grouptitle.toLowerCase()">
					</app-kpi-tile>
				</div>

				<!-- ── Ranked level ───────────────────────────────────────────── -->
				<div class="bsr-toolbar" *ngIf="!loading && !metaLoading && hasExportableRows">
					<button mat-stroked-button *ngIf="maxOpenDepth > 0" (click)="collapseAll()">
						<i class="material-icons bsr-drill-icon" aria-hidden="true">unfold_less</i>
						Collapse all
					</button>
					<span class="bsr-toolbar__spacer"></span>
					<mat-progress-spinner *ngIf="exporting" diameter="20" mode="indeterminate" color="accent">
					</mat-progress-spinner>
					<button mat-icon-button (click)="exportXls()" [disabled]="exporting || !hasExportableRows"
						matTooltip="Export to Excel" aria-label="Export to Excel">
						<i class="material-icons" aria-hidden="true">grid_on</i>
					</button>
					<button mat-icon-button (click)="exportPdf()" [disabled]="exporting || !hasExportableRows"
						matTooltip="Export to PDF" aria-label="Export to PDF">
						<i class="material-icons" aria-hidden="true">picture_as_pdf</i>
					</button>
				</div>

				<!-- #printTable is the export root; the exports mirror what is open. -->
				<div #printTable>
				<table class="feature-table feature-table--responsive" *ngIf="!loading && !metaLoading">
					<thead>
						<tr>
							<th>#</th>
							<th>{{ grouptitle }}</th>
							<th class="is-num">Net Sales</th>
							<th class="is-num">Share</th>
							<th class="is-num">Units</th>
							<th class="is-num">Invoices</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						<tr *ngFor="let n of visibleRows"
							[attr.data-depth]="n.depth"
							[class]="depthClass(n)"
							[class.bsr-child]="n.depth > 0"
							[class.bsr-open]="n.expanded"
							[class.bsr-groupstart]="isGroupStart(n)">
							<td data-label="Rank">
								<span class="bsr-rank" [class.bsr-rank--top]="rankOf(n) === 1">{{ rankOf(n) }}</span>
							</td>
							<td [attr.data-label]="n.dimensionTitle">
								<!-- One block for every row at every depth, so a child's name can
								     never end up rendering left of its own parent. -->
								<span class="bsr-nest" [style.marginLeft.px]="indentPx(n)">
									<span class="bsr-dim" *ngIf="n.depth > 0">{{ n.dimensionTitle }}</span>
									<strong>{{ n.label }}</strong>
									<div class="bsr-bar" aria-hidden="true">
										<div class="bsr-bar__fill" [style.width.%]="sharePct(n)"></div>
									</div>
								</span>
							</td>
							<td data-label="Net Sales" class="is-num">{{ n.net | currency }}</td>
							<td data-label="Share" class="is-num">{{ shareOfTotal(n) | number:'1.1-1' }}%</td>
							<td data-label="Units" class="is-num">{{ n.units | number:'1.0-0' }}</td>
							<td data-label="Invoices" class="is-num">{{ n.invoices | number:'1.0-0' }}</td>
							<td>
								<div class="bsr-rowactions">
									<mat-progress-spinner *ngIf="n.loading" diameter="18" mode="indeterminate" color="accent"></mat-progress-spinner>

									<button mat-stroked-button color="accent"
											*ngIf="!n.loading && canExpand(n)"
											(click)="expand(n)">
										<i class="material-icons bsr-drill-icon" aria-hidden="true">{{ isShowing(n, n.nextDim) ? 'expand_less' : 'expand_more' }}</i>
										{{ n.nextTitle }}
									</button>

									<button mat-icon-button
											*ngIf="!n.loading && n.dimension !== 'customer'"
											[attr.aria-label]="'Who buys this ' + n.dimensionTitle"
											[title]="'Who buys this ' + n.dimensionTitle"
											[class.bsr-who--on]="isShowing(n, 'customer')"
											(click)="showCustomers(n)">
										<i class="material-icons" aria-hidden="true">groups</i>
									</button>
								</div>
							</td>
						</tr>

						<tr class="is-total" *ngIf="roots.length">
							<td></td>
							<td [attr.data-label]="grouptitle"><strong>Total</strong></td>
							<td data-label="Net Sales" class="is-num"><strong>{{ totals.net | currency }}</strong></td>
							<td data-label="Share" class="is-num">100.0%</td>
							<td data-label="Units" class="is-num"><strong>{{ totals.units | number:'1.0-0' }}</strong></td>
							<td data-label="Invoices" class="is-num"><strong>{{ totals.invoices | number:'1.0-0' }}</strong></td>
							<td></td>
						</tr>

						<tr *ngIf="!roots.length">
							<td colspan="7" class="text-center text-muted">No sales in this period for the selected filters.</td>
						</tr>
					</tbody>
				</table>
				</div>

				<div *ngIf="loading || metaLoading" class="text-center my-4">
					<mat-progress-spinner diameter="32" mode="indeterminate" color="accent"></mat-progress-spinner>
				</div>

			</mat-card-content>
		</mat-card>
	</div>
</div>
